import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { SupportAttachmentPicker } from '@/pages/support/support-attachment-picker';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { resolveApiError } from '@/lib/api-errors';
import { hasPermission, isAdminOrOwner, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';
import {
  SupportTicketPriorityBadge,
  SupportTicketStatusBadge,
} from '@/pages/support/support-ticket-badges';
import { canChurchClose, canChurchReply } from '@/pages/support/support-ticket.rules';
import { RequestSupportSessionDialog } from '@/pages/support/request-support-session-dialog';
import { SupportTicketTimeline } from '@/pages/support/support-ticket-timeline';
import { formatTicketNumber, type SupportTicketTimeline as SupportTicketTimelineData } from '@/pages/support/types';
import { useSupportTicketUploads } from '@/pages/support/use-support-ticket-uploads';
import { usePreferences } from '@/stores/preferences-store';
import { formatDateTime } from '@/utils/format';

export const SupportTicketDetailPage = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { t } = useTranslation('support');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const membership = useCurrentMembership();
  const { locale, timezone } = usePreferences();

  const canReply = hasPermission(membership, PERMISSIONS.SUPPORT_TICKETS_REPLY);
  const canClose = hasPermission(membership, PERMISSIONS.SUPPORT_TICKETS_CREATE);
  const canRequestSession = isAdminOrOwner(membership);

  const [replyBody, setReplyBody] = useState('');
  const [closeOpen, setCloseOpen] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const uploads = useSupportTicketUploads();

  const timeline = useQuery({
    queryKey: ['support', 'tickets', ticketId, 'timeline'],
    enabled: Boolean(ticketId),
    queryFn: () => api.get<SupportTicketTimelineData>(`/support/tickets/${ticketId}/timeline`),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['support', 'tickets'] });
    void queryClient.invalidateQueries({ queryKey: ['support', 'tickets', ticketId] });
    void queryClient.invalidateQueries({ queryKey: ['support', 'tickets', ticketId, 'timeline'] });
  };

  const reply = useMutation({
    mutationFn: () =>
      api.post(`/support/tickets/${ticketId}/messages`, {
        body: replyBody.trim(),
        attachments: uploads.attachments,
      }),
    onSuccess: () => {
      toast.success(t('reply.sent'));
      setReplyBody('');
      uploads.reset();
      invalidate();
    },
    onError: (err) => toast.error(resolveApiError(err, tErrors)),
  });

  const closeTicket = useMutation({
    mutationFn: () => api.post(`/support/tickets/${ticketId}/close`, {}),
    onSuccess: () => {
      toast.success(t('close.success'));
      setCloseOpen(false);
      invalidate();
    },
    onError: (err) => toast.error(resolveApiError(err, tErrors)),
  });

  const ticket = timeline.data?.ticket;
  const showReply =
    ticket && canReply && canChurchReply(ticket) && ticket.status !== 'closed';
  const showClose = ticket && canClose && canChurchClose(ticket);

  const now = Date.now();
  const awaitingFirstReply =
    ticket &&
    !ticket.firstPlatformReplyAt &&
    ticket.status !== 'closed' &&
    ticket.status !== 'resolved';
  const firstReplyOverdue =
    awaitingFirstReply && new Date(ticket.slaFirstResponseDueAt).getTime() < now;
  const resolveOverdue =
    ticket &&
    ticket.status !== 'closed' &&
    ticket.status !== 'resolved' &&
    new Date(ticket.slaResolveDueAt).getTime() < now;

  return (
    <div>
      <PageHeader
        title={ticket ? t('detail.title', { number: formatTicketNumber(ticket.number) }) : t('detail.loadingTitle')}
        className="mb-2"
        breadcrumbs={[
          { label: t('title'), to: '/support/tickets' },
          {
            label: ticket ? t('ticketNumber', { number: formatTicketNumber(ticket.number) }) : t('detail.loadingTitle'),
          },
        ]}
        headerActions={
          canRequestSession || showClose ? (
            <>
              {canRequestSession ? (
                <Button variant="outline" onClick={() => setSessionOpen(true)}>
                  {t('session.request')}
                </Button>
              ) : null}
              {showClose ? (
                <Button variant="outline" onClick={() => setCloseOpen(true)}>
                  {t('close.action')}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {timeline.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-full max-w-md" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : null}

      {timeline.isError ? (
        <p className="text-body-sm text-danger">{tCommon('state.errorBody')}</p>
      ) : null}

      {ticket ? (
        <div className="space-y-6">
          <h2 className="text-section-title font-medium text-fg">{ticket.subject}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <SupportTicketStatusBadge status={ticket.status} />
            <SupportTicketPriorityBadge priority={ticket.priority} />
            <Badge variant="neutral">{t(`category.${ticket.category}`)}</Badge>
            {ticket.assignedPlatformName ? (
              <Badge variant="info">
                {t('detail.assigned', { name: ticket.assignedPlatformName })}
              </Badge>
            ) : null}
            {(() => {
              const notes = [
                ticket.status === 'awaiting_church'
                  ? { key: 'waiting', text: t('chips.waitingOnYou'), className: 'text-fg-secondary' }
                  : null,
                ticket.status === 'awaiting_platform' || ticket.status === 'open'
                  ? { key: 'support', text: t('chips.waitingOnSupport'), className: 'text-fg-secondary' }
                  : null,
                awaitingFirstReply
                  ? {
                      key: 'first',
                      text: firstReplyOverdue
                        ? t('chips.firstReplyOverdue')
                        : t('chips.firstReplyDue', {
                            date: formatDateTime(ticket.slaFirstResponseDueAt, { locale, timezone }),
                          }),
                      className: firstReplyOverdue ? 'text-danger' : 'text-fg-secondary',
                    }
                  : null,
                ticket.status !== 'closed' && ticket.status !== 'resolved'
                  ? {
                      key: 'resolve',
                      text: resolveOverdue
                        ? t('chips.resolveOverdue')
                        : t('chips.resolveDue', {
                            date: formatDateTime(ticket.slaResolveDueAt, { locale, timezone }),
                          }),
                      className: resolveOverdue ? 'text-warning' : 'text-fg-secondary',
                    }
                  : null,
              ].filter((note) => note !== null);
              if (notes.length === 0) return null;
              return (
                <p className="min-w-0 text-body-sm leading-5">
                  {notes.map((note, index) => (
                    <span key={note.key}>
                      {index > 0 ? <span className="text-fg-tertiary"> · </span> : null}
                      <span className={note.className}>{note.text}</span>
                    </span>
                  ))}
                </p>
              );
            })()}
          </div>

          <section className="rounded-lg border border-border p-4">
            <SupportTicketTimeline
              messages={timeline.data?.messages ?? []}
              events={timeline.data?.events ?? []}
            />
          </section>

          {showReply ? (
            <section className="rounded-lg border border-border p-4">
              <h2 className="mb-4 text-body font-medium text-fg">{t('reply.title')}</h2>
              <div className="flex flex-col gap-2">
                <FormField label={t('reply.message')} required>
                  {(field) => (
                    <Textarea
                      id={field.id}
                      value={replyBody}
                      onChange={(event) => setReplyBody(event.target.value)}
                      rows={4}
                      maxLength={10_000}
                      placeholder={t('reply.placeholder')}
                    />
                  )}
                </FormField>
                <div className="flex items-center gap-3">
                  <SupportAttachmentPicker
                    inlineHint
                    files={uploads.pending}
                    uploading={uploads.uploading}
                    progress={uploads.progress}
                    error={uploads.error}
                    disabled={uploads.uploading || uploads.atLimit}
                    onFiles={(files) => void uploads.addFiles(files)}
                    onRemove={uploads.removeAt}
                  />
                  <Button
                    className="ms-auto shrink-0"
                    loading={reply.isPending}
                    disabled={!replyBody.trim() || reply.isPending || uploads.uploading}
                    onClick={() => reply.mutate()}
                  >
                    {t('reply.send')}
                  </Button>
                </div>
              </div>
            </section>
          ) : null}
        </div>
      ) : null}

      {canRequestSession ? (
        <RequestSupportSessionDialog open={sessionOpen} onOpenChange={setSessionOpen} />
      ) : null}

      <ConfirmDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        title={t('close.confirmTitle')}
        description={t('close.confirmBody', {
          number: ticket ? formatTicketNumber(ticket.number) : '',
          subject: ticket?.subject ?? '',
        })}
        confirmLabel={t('close.action')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={closeTicket.isPending}
        onConfirm={() => closeTicket.mutate()}
      />
    </div>
  );
};
